/**
 * Bag/book item icons — ONE normalised icon set, one resolver.
 *
 * The bag, the hotbar previews and the market each used to build their own URL,
 * and two of the three guessed `/sprites/crops/produce_<id>.png` for EVERY id, so
 * every mob drop, animal loot and cooked food in the bag rendered as a broken
 * image. They also pulled from four different sprite homes whose art is not the
 * same shape (items 16×16, produce 16×32, mushroom 8×8), so with `object-contain`
 * a tall crop drew half the width of a drop and the mushroom drew 3.5× chunkier.
 *
 * Fix: `scripts/build-bag-icons.py` re-bakes every id below onto a common 32×32
 * canvas — content bbox-trimmed, integer-scaled so its LONGEST side fills the
 * canvas, then centred. Same apparent size, same centre, whatever the source
 * aspect. This module is the single resolver over that folder, and BAG_ITEM_IDS
 * is both the bake list (the python script parses it) and the test's id list.
 */

/**
 * Farm-column resources (backend forager.config FARM_COLUMN).
 *
 * `coal` is a Farm column too — mining drops it, the furnace burns it — and it was
 * missing from every one of these lists, so it had no bag row and no icon.
 */
export const RESOURCE_IDS = ['wood', 'stone', 'ore', 'gem', 'mushroom', 'coal'] as const;

/** Crops from backend field.config — each harvests into `produce_<id>`. */
export const CROP_IDS = [
  'apple', 'beet', 'cabbage', 'carrot', 'cherry', 'chili', 'corn', 'cucumber',
  'eggplant', 'garlic', 'grape', 'leek', 'onion', 'paprika', 'pea', 'peach',
  'pear', 'pepper', 'potato', 'pumpkin', 'purple_berry', 'red_berry',
  'strawberry', 'sunflower', 'tomato', 'turnip', 'watermelon', 'wheat',
] as const;

/** Animal loot + husbandry produce + cooked foods (fauna, husbandry, food configs). */
export const LOOT_IDS = [
  'raw_beef', 'raw_mutton', 'raw_meat',
  'cooked_beef', 'cooked_mutton', 'cooked_meat',
  'leather', 'feather', 'wool_white', 'wool_grey', 'wool_brown',
  'egg', 'milk',
] as const;

/**
 * Raw ore + smelted bars, one pair per metal (backend crafting/ores.config.ts).
 * `ore_<metal>` is what a vein drops; `bar_<metal>` is what the furnace makes and
 * what the crafting ladder spends. Both are Farm columns, so they show up in the
 * bag and on the ground exactly like wood and stone.
 */
export const ORE_IDS = [
  'ore_copper', 'ore_iron', 'ore_gold', 'ore_mythril',
  'bar_copper', 'bar_iron', 'bar_gold', 'bar_mythril',
  // Tiers 7-9, added 2026-08-23 — the pack's three unused ore rows as real
  // metals. Every id here must have a baked icon or the bag draws the backpack
  // placeholder for it (bag-icons.test.ts asserts each file exists).
  'ore_emerald', 'ore_amber', 'ore_ruby',
  'bar_emerald', 'bar_amber', 'bar_ruby',
] as const;

/**
 * Forage drops — things a tree or the ground gives you that are not a Farm column.
 *
 * `acorn` is the one the game actually grants today (woodcutting.config
 * ACORN_ITEM_ID: ~35% per felled tree, planted to grow a new tree). It was in no
 * icon list at all, so the bag drew the backpack placeholder for it.
 */
/**
 * Seeds — one bag row per crop, made by pressing produce (field seeds/extract) or
 * bought from the keeper.
 *
 * They were in NO icon list, so `itemIconSrc` fell through to the unknown-item
 * placeholder and a freshly pressed batch of carrot seeds appeared in the bag as a
 * backpack glyph labelled "Seed Carrot". Owner: "i've pressed seeds, but they
 * doesn't appeared in my bag" — they had, wearing the icon that means "no idea
 * what this is".
 */
export const SEED_IDS: string[] = CROP_IDS.map((c) => `seed_${c}`);

export const FORAGE_IDS = ['acorn'] as const;

/**
 * Fishing (backend fishing.config FISH_SPECIES + food.config).
 *
 * Seven species and ONE cooked fish: the pack's fish row has eight colours, and
 * the golden one is what a fish looks like off the fire rather than an eighth
 * species (scripts/build-fish-icons.py). The rod is here too — it lives in the
 * bag like any other item, which is how fishing checks you own one.
 */
export const FISH_IDS = [
  'fish_mudfish', 'fish_greyling', 'fish_reedfish', 'fish_bluefin',
  'fish_deepscale', 'fish_frostfin', 'fish_rubyscale',
  'cooked_fish', 'fishing_rod',
] as const;

/** Mob drops (drops/mobs configs). */
export const MOB_DROP_IDS = ['bone', 'rotten_flesh', 'orc_fang', 'imp_horn', 'ember', 'map_fragment'] as const;

/**
 * Every id the game can put in a bag. Mirrors the backend catalogue sources
 * (field.config crops, forager.config BASE_VALUE, food.config, husbandry, drops);
 * `bag-icons.test.ts` asserts each one resolves to a file that exists on disk.
 */
export const BAG_ITEM_IDS: string[] = [
  ...RESOURCE_IDS,
  ...ORE_IDS,
  ...CROP_IDS.map((c) => `produce_${c}`),
  ...SEED_IDS,
  ...LOOT_IDS,
  ...FORAGE_IDS,
  ...FISH_IDS,
  ...MOB_DROP_IDS,
];

const KNOWN = new Set(BAG_ITEM_IDS);

/** Deliberate "no art for this id" marker — never a broken image. */
export const UNKNOWN_ICON = '/sprites/icons/_unknown.png';

/** The normalised 32×32 icon for a bag item id. */
export function itemIconSrc(itemId: string): string {
  return KNOWN.has(itemId) ? `/sprites/icons/${itemId}.png` : UNKNOWN_ICON;
}

/**
 * Craft-slot icons for the book's Equipped page. `sword` and `bow` had no baked
 * frame at all (the sword art shipped under the name `tool_knife`), so both slots
 * rendered broken; the slicer now bakes tool_sword/tool_bow from the pack.
 */
export const CRAFT_SLOT_ICON: Record<string, string> = {
  axe: '/sprites/ui/hotbar/tool_axe.png',
  pickaxe: '/sprites/ui/hotbar/tool_pickaxe.png',
  hoe: '/sprites/ui/hotbar/tool_hoe.png',
  sword: '/sprites/ui/hotbar/tool_sword.png',
  bow: '/sprites/ui/hotbar/tool_bow.png',
};

export const craftSlotIconSrc = (slot: string): string => CRAFT_SLOT_ICON[slot] ?? UNKNOWN_ICON;

/**
 * The armour the hero is ACTUALLY wearing, for the bag's and the anvil's slots.
 *
 * Both the tier→art mapping and the icon URL come from ui/armor-art.ts, the one
 * function the paper-doll also dresses from — re-exported here because the bag
 * and crafting panel already import their icons from this module. There is no
 * second table any more: the previous `${slot}_${metal}.png` naming was derived
 * from a copy of the ladder kept in build-armor-icons.py, which is how a heavy
 * helm on the hero could still be a light helm in the bag.
 *
 * These are never run through tintIcon: the metal is in the art, and a tint layer
 * on top fights the sprite's own shading.
 */
export { armourIconSrc, armourLayerFile } from './armor-art';

/** Legacy pictograms, still used where a SLOT is named rather than a piece shown. */
export const ARMOUR_SLOT_ICON: Record<string, string> = {
  helmet: '/sprites/ui/icons/helmet.png',
  chest: '/sprites/ui/icons/chestplate.png',
  legs: '/sprites/ui/icons/legs.png',
};

export const armourSlotIconSrc = (slot: string): string => ARMOUR_SLOT_ICON[slot] ?? UNKNOWN_ICON;

/** The bag board's paging arrows — pack UI carets, not glyphs from a system font. */
export const PAGER_ICON = { prev: '/sprites/ui/icons/caret_left.png', next: '/sprites/ui/icons/caret_right.png' } as const;
